import {
  HiOutlineBolt,
  HiOutlineCheckBadge,
  HiOutlineFolder,
  HiOutlineFolderPlus,
  HiOutlineInformationCircle,
  HiOutlineSquare2Stack,
} from 'react-icons/hi2';
import {
  AddableStepType,
  DataInput,
  DataInputType,
  StepCreationPreset,
  StepType,
} from '@/types/automationFields.ts';
import { CustomSVG } from '@/types/common.ts';
import { IconType } from 'react-icons';
import TransformDataIcon from '@/assets/icons/transform_data.svg?react';
import ExtractDataIcon from '@/assets/icons/extract_data.svg?react';
import IterateStepIcon from '@/assets/icons/iterate_step.svg?react';
import RepeatStepIcon from '@/assets/icons/repeat_step.svg?react';
import ConditionalStepIcon from '@/assets/icons/conditional_step.svg?react';

import CreateExcelFileIcon from '@/assets/step_creation_preset_icons/create_excel_file.svg?react';
import CreateWordFileIcon from '@/assets/step_creation_preset_icons/create_word_file.svg?react';
import WebBrowserIcon from '@/assets/step_creation_preset_icons/web_browser.svg?react';
import WebResearchIcon from '@/assets/step_creation_preset_icons/web_research.svg?react';

import { HiOutlineFilter, HiOutlineShare } from 'react-icons/hi';

export interface StepTypeDefinition {
  key: AddableStepType;
  label: string;
  description: string;
  icon?: IconType;
  svg?: CustomSVG;
  iconBackgroundColor: string;
  iconColor: string;
}

export interface StepTypePickerCategory {
  title: string;
  types: AddableStepType[];
  collapsedByDefault?: boolean;
}

export const STEP_TYPES_BY_CARD_CATEGORY: Record<string, AddableStepType[]> = {
  Trigger: [StepType.Trigger],
  'Data Actions': [
    StepType.TransformData,
    StepType.ExtractData,
    StepType.SubAutomation,
    StepType.RunMetadata,
    StepCreationPreset.WebResearch,
    StepCreationPreset.BrowserAutomation,
    StepCreationPreset.RunOptimizationSolver,
  ],
  Logic: [
    StepType.Iterate,
    StepType.Repeat,
    StepType.Retry,
    StepType.FailureHandler,
    StepType.Conditional,
    StepType.Filter,
    StepType.Validate,
    StepType.HumanDecisionGate,
  ],
  'External Actions': [StepType.Dispatch],
  'File Actions': [
    StepCreationPreset.CreateCsvFile,
    StepCreationPreset.CreateExcelFile,
    StepCreationPreset.BuildExcelWorkbook,
    StepCreationPreset.ExcelWorkbookToJson,
    StepCreationPreset.CreateWordFile,
    StepType.CreateDirectory,
    StepType.GetFileFromDirectory,
  ],
};

export const STEP_TYPE_PICKER_CATEGORIES: StepTypePickerCategory[] = [
  {
    title: 'Data Actions',
    types: [
      StepType.TransformData,
      StepType.ExtractData,
      StepType.SubAutomation,
      StepType.LocalSubflowCall,
      StepCreationPreset.WebResearch,
      StepCreationPreset.BrowserAutomation,
    ],
  },
  {
    title: 'Logic',
    types: [
      StepType.Iterate,
      StepType.Repeat,
      StepType.Retry,
      StepType.FailureHandler,
      StepType.Conditional,
      StepType.Filter,
      StepType.Validate,
    ],
  },
  {
    title: 'External Actions',
    types: [StepType.Dispatch],
  },
  {
    title: 'Document Actions',
    types: [
      StepCreationPreset.CreateCsvFile,
      StepCreationPreset.CreateExcelFile,
      StepCreationPreset.BuildExcelWorkbook,
      StepCreationPreset.ExcelWorkbookToJson,
      StepCreationPreset.CreateWordFile,
    ],
  },
  {
    title: 'Less Common',
    types: [
      StepType.RunMetadata,
      StepCreationPreset.RunOptimizationSolver,
      StepType.CreateDirectory,
      StepType.GetFileFromDirectory,
    ],
    collapsedByDefault: true,
  },
];

export const STEP_TYPE_UI_CONFIG: StepTypeDefinition[] = [
  {
    key: StepType.Trigger,
    label: 'Trigger',
    description:
      'A trigger is the starting point of your automation and is based on external events like a new email or a manual trigger',
    icon: HiOutlineBolt,
    iconBackgroundColor: '#FEF6B2',
    iconColor: '#C6B200',
  },
  {
    key: StepType.ExtractData,
    label: 'Find Info',
    description: 'Extract key pieces of information from documents',
    svg: ExtractDataIcon,
    iconBackgroundColor: '#D0D2FE',
    iconColor: '#454CFC',
  },
  {
    key: StepType.TransformData,
    label: 'Transform Data',
    description: 'Use AI to restructure data like text and numbers. Very flexible step!',
    svg: TransformDataIcon,
    iconBackgroundColor: '#D0D2FE',
    iconColor: '#454CFC',
  },
  {
    key: StepType.SubAutomation,
    label: 'Sub-Automation',
    description: 'Queue or wait on another automation from inside the current run',
    icon: HiOutlineSquare2Stack,
    iconBackgroundColor: '#D0D2FE',
    iconColor: '#454CFC',
  },
  {
    key: StepType.LocalSubflowCall,
    label: 'Local Subflow',
    description: 'Create a private, typed module owned by this automation version',
    icon: HiOutlineSquare2Stack,
    iconBackgroundColor: '#D0D2FE',
    iconColor: '#454CFC',
  },
  {
    key: StepType.RunMetadata,
    label: 'Run Info',
    description: 'Create a JSON dataset with current run and execution metadata',
    icon: HiOutlineInformationCircle,
    iconBackgroundColor: '#E3F2ED',
    iconColor: '#27765E',
  },
  {
    key: StepType.Iterate,
    label: 'Loop over Data',
    description: 'Do the same action to each item in a list',
    svg: IterateStepIcon,
    iconBackgroundColor: '#FBD1AB',
    iconColor: '#E2710B',
  },
  {
    key: StepType.Repeat,
    label: 'Repeat Steps',
    description: 'Repeat the same action multiple times',
    svg: RepeatStepIcon,
    iconBackgroundColor: '#FBD1AB',
    iconColor: '#E2710B',
  },
  {
    key: StepType.Retry,
    label: 'Retry Scope',
    description: 'Group related steps into one child scope that can be retried together',
    icon: HiOutlineSquare2Stack,
    iconBackgroundColor: '#FBD1AB',
    iconColor: '#E2710B',
  },
  {
    key: StepType.FailureHandler,
    label: 'Handle errors',
    description: 'Run response steps when a group of steps fails',
    icon: HiOutlineSquare2Stack,
    iconBackgroundColor: '#FBD1AB',
    iconColor: '#C2410C',
  },
  {
    key: StepType.Conditional,
    label: 'Paths',
    description: 'Specify rules to decide which path your automation will follow',
    svg: ConditionalStepIcon,
    iconBackgroundColor: '#FBD1AB',
    iconColor: '#E2710B',
  },
  {
    key: StepType.Dispatch,
    label: 'External Actions',
    description: 'Send or pull data from Airtable, Google Sheets and more',
    icon: HiOutlineShare,
    iconBackgroundColor: '#CBF1D6',
    iconColor: '#1B9D41',
  },
  {
    key: StepType.CreateDirectory,
    label: 'Create Folder',
    description: 'Combines files together into a folder',
    icon: HiOutlineFolderPlus,
    iconBackgroundColor: '#ECECEC',
    iconColor: '#565656',
  },
  {
    key: StepType.GetFileFromDirectory,
    label: 'Get File from Folder',
    description: 'Retrieves a specific file from within a folder',
    icon: HiOutlineFolder,
    iconBackgroundColor: '#ECECEC',
    iconColor: '#565656',
  },
  {
    key: StepType.Filter,
    label: 'Filter Data',
    description: 'Filter out irrelevant files or pages from your data',
    icon: HiOutlineFilter,
    iconBackgroundColor: '#FBD1AB',
    iconColor: '#E2710B',
  },
  {
    key: StepType.Validate,
    label: 'Verify Data',
    description: 'Ensure data meets specific criteria before proceeding',
    icon: HiOutlineCheckBadge,
    iconBackgroundColor: '#FBD1AB',
    iconColor: '#E2710B',
  },
  {
    key: StepType.HumanDecisionGate,
    label: 'Human Decision Gate',
    description: 'Pause for an authorized reviewer to make a typed decision',
    icon: HiOutlineCheckBadge,
    iconBackgroundColor: '#FBD1AB',
    iconColor: '#E2710B',
  },
  {
    key: StepCreationPreset.CreateCsvFile,
    label: 'Create CSV File',
    description: 'Export your data into a CSV file',
    svg: CreateExcelFileIcon,
    iconBackgroundColor: '#ECECEC',
    iconColor: '#565656',
  },
  {
    key: StepCreationPreset.CreateExcelFile,
    label: 'Create Excel File',
    description: 'Export your data into an Excel spreadsheet or workbook',
    svg: CreateExcelFileIcon,
    iconBackgroundColor: '#ECECEC',
    iconColor: '#565656',
  },
  {
    key: StepCreationPreset.BuildExcelWorkbook,
    label: 'Build Excel Workbook',
    description: 'Build or update an Excel workbook when the output must preserve workbook structure',
    svg: CreateExcelFileIcon,
    iconBackgroundColor: '#ECECEC',
    iconColor: '#565656',
  },
  {
    key: StepCreationPreset.ExcelWorkbookToJson,
    label: 'Spreadsheet Workbook to JSON',
    description: 'Convert an Excel or OpenDocument spreadsheet workbook into one structured JSON dataset',
    svg: CreateExcelFileIcon,
    iconBackgroundColor: '#ECECEC',
    iconColor: '#565656',
  },
  {
    key: StepCreationPreset.CreateWordFile,
    label: 'Create Word Doc File',
    description: 'Export your data into an word document',
    svg: CreateWordFileIcon,
    iconBackgroundColor: '#ECECEC',
    iconColor: '#565656',
  },
  {
    key: StepCreationPreset.WebResearch,
    label: 'Web Research',
    description: 'Leverage the web to perform deep research on a topic or question',
    svg: WebResearchIcon,
    iconBackgroundColor: '#D0D2FE',
    iconColor: '#454CFC',
  },
  {
    key: StepCreationPreset.BrowserAutomation,
    label: 'Browser Automation',
    description: 'Navigate sites, fill out forms, and complete transactions',
    svg: WebBrowserIcon,
    iconBackgroundColor: '#D0D2FE',
    iconColor: '#454CFC',
  },
  {
    key: StepCreationPreset.RunOptimizationSolver,
    label: 'Run Optimization Solver',
    description: 'Build and solve a constrained production schedule',
    icon: HiOutlineBolt,
    iconBackgroundColor: '#CBF1D6',
    iconColor: '#1B9D41',
  },
];

export const emptyDataInput: DataInput = {
  name: null,
  last_known_display_name: null,
  data_input_type: DataInputType.Static,
  config: { static_dataset_id: null },
};
